import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Chip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import YoutubeIcon from '@/components/shared/icons/youtube-icon';
import { getMasterPhotoById, getTeamImageById } from '@/helpers/images-helper';
import CustomSectionWrapper from '@/DesignSystem/wrappers/CustomSectionWrapper';
import { obtenerEquipo } from '@/data/equipos';

const RecomendacionesModal = ({ open, onClose, recomendacion, onYoutubeClick }) => {
    const [selectedTeamTab, setSelectedTeamTab] = useState(0);

    if (!recomendacion) {
        return null;
    }

    const teamIds = Object.keys(recomendacion.teams || {});
    const hasMultipleTeams = teamIds.length > 1;
    const selectedTeamId = teamIds[selectedTeamTab];
    const selectedTeamCategories = recomendacion.teams[selectedTeamId] || [];

    const handleTeamTabChange = (event, newValue) => {
        setSelectedTeamTab(newValue);
    };

    // Función para extraer el videoId de una URL de YouTube
    const getVideoId = (url) => {
        if (!url) return null;
        try {
            const urlObj = new URL(url);
            let videoId;

            // Verificar si la URL es de youtu.be o youtube.com
            if (urlObj.hostname.includes('youtu.be')) {
                videoId = urlObj.pathname.slice(1); // Extraer el ID del video
            } else if (urlObj.hostname.includes('youtube.com')) {
                videoId = urlObj.searchParams.get('v');
            }

            return videoId;
        } catch (error) {
            console.error('Error al procesar la URL del video:', error);
            return null;
        }
    };

    const handleYoutubeClick = () => {
        onClose(); // Cerrar el modal de recomendación
        onYoutubeClick(recomendacion.youtubeUrl); // Notificar al componente padre
    };

    const videoId = getVideoId(recomendacion.youtubeUrl);

    const getTeamName = (teamId) => {
        var teamFound = obtenerEquipo(teamId);
        return (
            <Box display={'flex'} alignItems={'center'} gap={0.5}>
                <CustomImageComponent
                    src={getTeamImageById(teamId, 45)}
                    alt={teamFound?.name || 'Equipo'}
                    width={20}
                    height={20}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                    {teamFound?.name}
                </Typography>
            </Box>
        );
    };

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title=""
            maxWidth="md"
            width="95%"
            paperSx={{
                minHeight: '500px',
            }}
            contentSx={{ pt: 0 }}
            padding={0}
            hideHeader={true}
            customHeader={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                    }}
                >
                    <CustomImageComponent
                        src={recomendacion.author?.image}
                        alt={recomendacion.author?.nickname || 'Autor'}
                        width={60}
                        height={60}
                        style={{
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid',
                            borderColor: 'primary.main',
                        }}
                    />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {recomendacion.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Por {recomendacion.author?.nickname || 'Autor desconocido'}
                        </Typography>
                        {recomendacion.description && (
                            <Typography variant="body2" color="text.secondary">
                                {recomendacion.description}
                            </Typography>
                        )}
                    </Box>

                    <IconButton onClick={onClose} sx={{ color: 'text.primary' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            }
        >
            {/* Body con tabs para equipos (si hay múltiples) */}
            <Box sx={{ p: 1 }}>
                {hasMultipleTeams ? (
                    <>
                        {/* Tabs para equipos */}
                        <Tabs
                            value={selectedTeamTab}
                            onChange={handleTeamTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                mb: 1,
                                minHeight: 'auto',
                                '& .MuiTab-root': {
                                    minHeight: 'auto',
                                    padding: '8px 12px',
                                    fontSize: '0.875rem',
                                },
                                '& .MuiTabs-indicator': {
                                    height: '2px',
                                },
                            }}
                        >
                            {teamIds.map((teamId, index) => (
                                <Tab
                                    key={teamId}
                                    label={getTeamName(teamId)}
                                    sx={{
                                        textTransform: 'none',
                                        minHeight: 'auto',
                                        padding: '8px 12px',
                                        fontSize: '0.875rem',
                                    }}
                                />
                            ))}
                        </Tabs>

                        {selectedTeamCategories.map((category, index) => (
                            <CategorySection key={index} category={category} />
                        ))}
                    </>
                ) : (
                    /* Contenido directo si hay un solo equipo */
                    <Box>
                        {videoId && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    padding: '8px 12px',
                                    borderRadius: 1,
                                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                    border: '1px solid rgba(255, 0, 0, 0.2)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 0, 0, 0.15)',
                                        transform: 'translateY(-1px)',
                                    },
                                    mb: 1,
                                }}
                                onClick={handleYoutubeClick}
                            >
                                <YoutubeIcon width={20} height={20} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        color: 'error.main',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    Ver video relacionado
                                </Typography>
                            </Box>
                        )}
                        {selectedTeamCategories.map((category, index) => (
                            <CategorySection key={index} category={category} />
                        ))}
                    </Box>
                )}
            </Box>
        </CustomModal>
    );
};

const CategorySection = ({ category }) => {
    return (
        <CustomSectionWrapper title={category.name} color="accent2" mb={1}>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {category.players.map((player, index) => (
                    <PlayerCard key={index} player={player} />
                ))}
            </Box>
        </CustomSectionWrapper>
    );
};

const PlayerCard = ({ player }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: 60,
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                },
            }}
        >
            <CustomImageComponent
                src={getMasterPhotoById(player.playerId, 90)}
                alt={player.playerName}
                width={60}
                height={60}
                style={{
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid',
                    borderColor: 'primary.light',
                    marginBottom: '4px',
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    textAlign: 'center',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                }}
            >
                {player.playerName}
            </Typography>
        </Box>
    );
};

export default RecomendacionesModal;
